import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { mdiCloudUpload } from '@mdi/js';
import Icon from '@mdi/react';
import { setDndActive } from '../../../redux/general/sliceDragDrop';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { getFileExtensionsFromFormats } from '../../../util/FileUtils';
import { defaultAllowedFileFormat } from '../../../Constants';

interface IDropzoneProps {
    containerRef: React.MutableRefObject<HTMLElement>;
    onDropFiles: (files: File[]) => void;
    overrideConfig?: IDropzoneConfig;
}

interface IDropzoneConfig {
    acceptFileTypes: string[];
    appearance: {
        default: IDropzoneAppearance;
        accept: IDropzoneAppearance;
    };
}

interface IDropzoneAppearance {
    color: string;
    background: string;
    displayText: string;
}

type DropzoneStateKeys = 'default' | 'accept';

type DropzoneState = {
    key: DropzoneStateKeys;
    filesCounts?: { total: number; accepted: number };
};

const defaultConfig: IDropzoneConfig = {
    acceptFileTypes: getFileExtensionsFromFormats([defaultAllowedFileFormat]),
    appearance: {
        default: {
            color: '#44AC34', // light green
            background: '#FFFFFFFF', // transparent
            displayText: 'defaultText', // i18n language key
        },
        accept: {
            color: '#008000', // green
            background: '#00800030', // transparent green
            displayText: 'defaultText', // i18n language key
        },
    },
};

export const resetFnConfig: { resetFn?: () => void } = {
    resetFn: undefined,
};

let dragEnterCount = 0;

export default (props: IDropzoneProps) => {
    const config = props.overrideConfig ?? defaultConfig;

    const dispatch = useDispatch();
    const { t } = useTranslation('translations', { keyPrefix: 'dropzone' });
    const dndIntent = useSelector((_store: IAppReduxStore) => _store.dragDrop.dndIntent);
    const fileTypes = useSelector((_store: IAppReduxStore) => _store.projectFileTypes.data);

    const inputRef = useRef<HTMLInputElement>();
    const dropzoneRef = useRef<HTMLDivElement>();

    const [dropzoneState, setDropzoneState] = useState<DropzoneState>({ key: 'default' });
    const [dropzoneAppearance, setDropzoneAppearance] = useState<IDropzoneAppearance>(
        config.appearance.default,
    );

    resetFnConfig.resetFn = () => {
        if (inputRef.current) {
            inputRef.current.value = null;
        }
    };

    const onWindowDragEnter = () => {
        dispatch(setDndActive('fileUpload'));
    };

    useEffect(() => {
        defaultConfig.acceptFileTypes = getFileExtensionsFromFormats([defaultAllowedFileFormat]);
    }, [fileTypes]);

    useEffect(() => {
        window.addEventListener('dragenter', onWindowDragEnter, { once: true });
        return () => {
            window.removeEventListener('dragenter', onWindowDragEnter);
        };
    }, []);

    useEffect(() => {
        switch (dropzoneState.key) {
            case 'default':
                setDropzoneAppearance({
                    ...config.appearance.default,
                    displayText: t(config.appearance.default.displayText),
                });
                setDropzoneCursor('default');
                break;
            case 'accept':
                setDropzoneAppearance({
                    ...config.appearance.accept,
                    displayText: t(config.appearance.accept.displayText),
                });
                setDropzoneCursor('default');
                break;
        }
    }, [dropzoneState]);

    const onDragEnter = () => {
        if (dndIntent !== 'fileUpload') {
            return;
        }

        dragEnterCount++;
        setDropzoneState({ key: 'accept' });
    };

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (dndIntent !== 'fileUpload') {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        e.dataTransfer.dropEffect = 'copy';
    };

    const onInputFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onDropFiles(Array.from(e.target.files));
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (dndIntent !== 'fileUpload') {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        setDropzoneState({ key: 'default' });
        props.onDropFiles(Array.from(e.dataTransfer.files));
    };

    const onDragLeave = () => {
        dragEnterCount--;

        if (dragEnterCount === 0) {
            setDropzoneState({ key: 'default' });
        }
    };

    const setDropzoneCursor = (cursorStyle: string) => {
        dropzoneRef.current.style.cursor = cursorStyle;
    };

    const onBrowseFiles = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        (inputRef.current as HTMLInputElement).click();
    };

    const renderFileDialogLink = () => {
        return (
            <>
                <a
                    href='#'
                    onClick={onBrowseFiles}
                    style={{ color: 'royalblue', alignSelf: 'start', fontSize: 'small' }}
                >
                    {t('browseFiles')}
                </a>
                <input
                    ref={inputRef}
                    hidden
                    multiple
                    type='file'
                    accept={config.acceptFileTypes.join(',')}
                    onChange={onInputFiles}
                />
            </>
        );
    };

    return (
        <div
            id='dropzone'
            ref={dropzoneRef}
            style={{
                bottom: '5%',
                width: '100%',
                padding: '20px',
                border: `2px dashed ${dropzoneAppearance.color}`,
                fontSize: '20px',
                fontWeight: 'bold',
                color: dropzoneAppearance.color,
                textAlign: 'center',
                background: dropzoneAppearance.background,
                borderRadius: '5px',
            }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Stack direction='row'>
                    <Icon path={mdiCloudUpload} size={2} style={{ marginRight: '12px' }} />
                    <Stack direction='column'>
                        {dropzoneAppearance.displayText}
                        <br />
                        {renderFileDialogLink()}
                    </Stack>
                </Stack>
            </Box>
        </div>
    );
};
